import { useState } from "react";
import { CgClose } from "react-icons/cg";
import useCatalogSearch from "../Commaon/useCatalogSearch";
import { MAX_SUGGESTION_ITEMS } from "./prescriptionUtils";

const itemKey = (item) => `${item.type}:${item.itemId}`;

const toFormItems = (suggestion) =>
  (suggestion?.items || []).map((item) => ({
    type: item.type,
    itemId: String(item.itemId),
    title: item.title || String(item.itemId),
    bestPrice: item.bestPrice,
    isAvailable: item.isAvailable !== false,
  }));

const formatPrice = (value) => (value === undefined || value === null || value === "" ? "" : `₹${value}`);

/**
 * Note for the user + tests/packages picked from the catalog.
 * Pre-filled from the existing suggestion; `onSubmit({ note, items })` returns a promise.
 */
const SuggestionForm = ({ suggestion, disabled, saving, onSubmit }) => {
  const [note, setNote] = useState(suggestion?.note || "");
  const [items, setItems] = useState(() => toFormItems(suggestion));
  const [kind, setKind] = useState("test");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const { results, searching } = useCatalogSearch(kind, search, !disabled);

  const selectedKeys = new Set(items.map(itemKey));
  const isFull = items.length >= MAX_SUGGESTION_ITEMS;

  const addItem = (result) => {
    const next = { type: kind, itemId: String(result._id), title: result.title || String(result._id), bestPrice: result.bestPrice, isAvailable: true };
    if (selectedKeys.has(itemKey(next)) || isFull) return;
    setItems((prev) => [...prev, next]);
    setError("");
  };

  const removeItem = (key) => setItems((prev) => prev.filter((item) => itemKey(item) !== key));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!note.trim() && !items.length) {
      setError("Add a note or at least one test/package");
      return;
    }
    if (items.length > MAX_SUGGESTION_ITEMS) {
      setError(`At most ${MAX_SUGGESTION_ITEMS} items can be suggested`);
      return;
    }
    setError("");
    await onSubmit({
      note: note.trim(),
      items: items.map(({ type, itemId }) => ({ type, itemId })),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={disabled || saving}>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Note to the user</label>
          <textarea
            className="form-control"
            rows={3}
            placeholder="e.g. Based on your prescription we recommend the following tests"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <label className="form-label small fw-semibold d-flex justify-content-between">
          <span>Suggested tests &amp; packages</span>
          <span className={`fw-normal ${isFull ? "text-danger" : "text-muted"}`}>
            {items.length}/{MAX_SUGGESTION_ITEMS}
          </span>
        </label>

        {items.length > 0 ? (
          <ul className="list-group mb-2">
            {items.map((item) => (
              <li key={itemKey(item)} className="list-group-item d-flex align-items-center gap-2 py-1 px-2 small">
                <span className={`badge ${item.type === "package" ? "bg-info text-dark" : "bg-light text-dark border"}`}>
                  {item.type === "package" ? "Package" : "Test"}
                </span>
                <span className="flex-grow-1 text-truncate" title={item.title}>
                  {item.title}
                  {!item.isAvailable && <span className="text-danger ms-1">(no longer available)</span>}
                </span>
                {formatPrice(item.bestPrice) && <span className="text-muted">{formatPrice(item.bestPrice)}</span>}
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger p-0"
                  onClick={() => removeItem(itemKey(item))}
                  aria-label={`Remove ${item.title}`}
                >
                  <CgClose size={16} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-muted small mb-2">No items added yet</div>
        )}

        <div className="input-group input-group-sm">
          <select
            className="form-select"
            style={{ maxWidth: "120px" }}
            value={kind}
            onChange={(event) => setKind(event.target.value)}
            aria-label="Item type"
          >
            <option value="test">Tests</option>
            <option value="package">Packages</option>
          </select>
          <input
            type="text"
            className="form-control"
            placeholder={isFull ? `Maximum ${MAX_SUGGESTION_ITEMS} items reached` : `Search ${kind}s by name...`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            disabled={isFull}
          />
        </div>
        {!disabled && !isFull && (
          <div className="list-group mt-1" style={{ maxHeight: "180px", overflowY: "auto" }}>
            {searching && <div className="list-group-item small text-muted">Searching...</div>}
            {!searching && results.length === 0 && (
              <div className="list-group-item small text-muted">No active {kind}s found</div>
            )}
            {!searching &&
              results.map((result) => {
                const added = selectedKeys.has(`${kind}:${result._id}`);
                return (
                  <button
                    key={result._id}
                    type="button"
                    className="list-group-item list-group-item-action small d-flex justify-content-between"
                    onClick={() => addItem(result)}
                    disabled={added}
                  >
                    <span className="text-truncate">{result.title || result._id}</span>
                    <span className="text-muted ms-2 flex-shrink-0">
                      {added ? "Added" : formatPrice(result.bestPrice)}
                    </span>
                  </button>
                );
              })}
          </div>
        )}

        {error && <div className="text-danger small mt-2">{error}</div>}

        <div className="d-flex justify-content-end mt-3">
          <button type="submit" className="btn btn-primary btn-sm">
            {suggestion ? "Update suggestion" : "Send suggestion"}
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default SuggestionForm;
